/**
 * ImmersiveWorkoutAudioSettings - Simple audio toggle and voice picker
 */
import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { useRootStore } from '../../../../Store/useRootStore';
import { useTrans } from '../../../../Store/System/LocalizationStore';
import { CoachTtsVoice, CoachTtsVoiceApi } from '../../../../Model/TextToSpeech/CoachTtsVoice';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 500;
`;

const Panel = styled.div`
  background: #1a1a1a;
  border-radius: 20px;
  padding: 24px;
  width: 340px;
  max-width: 90vw;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;

  & > i {
    font-size: 24px;
    color: ${Theme.Colors.primary};
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  width: 52px;
  height: 28px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 14px;
    transition: 0.3s;

    &::before {
      content: '';
      position: absolute;
      width: 22px;
      height: 22px;
      left: 3px;
      bottom: 3px;
      background: #fff;
      border-radius: 50%;
      transition: 0.3s;
    }
  }

  input:checked + span {
    background: ${Theme.Colors.primary};
  }

  input:checked + span::before {
    transform: translateX(24px);
  }
`;

const VoiceSection = styled.div`
  margin-top: 16px;
`;

const VoiceSectionTitle = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const VoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VoiceItem = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ selected }) => (selected ? `${Theme.Colors.primary}30` : 'rgba(255, 255, 255, 0.08)')};
  border: 1px solid ${({ selected }) => (selected ? Theme.Colors.primary : 'transparent')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ selected }) => (selected ? `${Theme.Colors.primary}40` : 'rgba(255, 255, 255, 0.12)')};
  }
`;

const VoiceName = styled.span<{ selected?: boolean }>`
  color: ${({ selected }) => (selected ? Theme.Colors.primary : '#fff')};
  font-size: 15px;
  font-weight: ${({ selected }) => (selected ? 600 : 500)};
`;

const PlayButton = styled.button<{ playing?: boolean }>`
  background: ${({ playing }) => (playing ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.15)')};
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background: ${({ playing }) => (playing ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.25)')};
  }
`;

export type ImmersiveWorkoutAudioSettingsProps = {
  onClose: () => void;
};

export const ImmersiveWorkoutAudioSettings: React.FC<ImmersiveWorkoutAudioSettingsProps> = observer(({ onClose }) => {
  const { t } = useTrans();
  const { remoteSettings, language } = useRootStore();

  const [voices, setVoices] = useState<CoachTtsVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<CoachTtsVoice | undefined>();
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);

  const isAudioEnabled = remoteSettings.get('audioFeedback') !== false;
  const isAICoachEnabled = remoteSettings.get('aiCoachEnabled') !== false;

  useEffect(() => {
    CoachTtsVoiceApi.list().then((res) => setVoices(res));
    CoachTtsVoiceApi.current().then((res) => setSelectedVoice(res));
  }, []);

  const handleToggleAudio = useCallback(
    (enabled: boolean) => {
      remoteSettings.set('audioFeedback', enabled);
    },
    [remoteSettings],
  );

  const handleToggleAICoach = useCallback(
    (enabled: boolean) => {
      remoteSettings.set('aiCoachEnabled', enabled);
    },
    [remoteSettings],
  );

  const handleSelectVoice = useCallback(
    (voice: CoachTtsVoice) => {
      remoteSettings.set('voiceIdentifier', voice.identifier);
      setSelectedVoice(voice);
    },
    [remoteSettings],
  );

  const handlePreviewVoice = useCallback(
    async (voice: CoachTtsVoice, e: React.MouseEvent) => {
      e.stopPropagation();
      
      if (playingVoiceId === voice.identifier) {
        previewAudio?.pause();
        setPlayingVoiceId(null);
        setPreviewAudio(null);
        return;
      }

      if (previewAudio) {
        previewAudio.pause();
      }

      try {
        const response = await HttpBackend.post(`/coach/text-to-speech/voice_demo_${voice.identifier}/generate`, {
          voiceIdentifier: voice.identifier,
          language: language,
        });
        const url = response?.url;
        if (url) {
          const audioEl = new Audio(url);
          audioEl.onended = () => {
            setPlayingVoiceId(null);
            setPreviewAudio(null);
          };
          await audioEl.play();
          setPlayingVoiceId(voice.identifier);
          setPreviewAudio(audioEl);
        }
      } catch (error) {
        console.error('Failed to play voice demo', error);
      }
    },
    [playingVoiceId, previewAudio, language],
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Overlay onClick={handleOverlayClick}>
      <Panel>
        <Header>
          <Title>{t('workout.audioSettings', { defaultValue: 'Audio Settings' })}</Title>
          <CloseButton onClick={onClose}>
            <i className="mdi mdi-close" />
          </CloseButton>
        </Header>

        <ToggleRow>
          <ToggleLabel>
            <i className="mdi mdi-volume-high" />
            {t('workout.coachAudio', { defaultValue: 'Coach Audio' })}
          </ToggleLabel>
          <ToggleSwitch>
            <input type="checkbox" checked={isAudioEnabled} onChange={(e) => handleToggleAudio(e.target.checked)} />
            <span />
          </ToggleSwitch>
        </ToggleRow>

        {isAudioEnabled && (
          <ToggleRow>
            <ToggleLabel>
              <i className="mdi mdi-robot" />
              {t('workout.aiCoach', { defaultValue: 'AI Coach' })}
            </ToggleLabel>
            <ToggleSwitch>
              <input type="checkbox" checked={isAICoachEnabled} onChange={(e) => handleToggleAICoach(e.target.checked)} />
              <span />
            </ToggleSwitch>
          </ToggleRow>
        )}

        {isAudioEnabled && voices.length > 0 && (
          <VoiceSection>
            <VoiceSectionTitle>{t('workout.selectVoice', { defaultValue: 'Select Voice' })}</VoiceSectionTitle>
            <VoiceList>
              {voices.map((voice) => {
                const isSelected = selectedVoice?.identifier === voice.identifier;
                const isPlaying = playingVoiceId === voice.identifier;
                return (
                  <VoiceItem key={voice.identifier} selected={isSelected} onClick={() => handleSelectVoice(voice)}>
                    <VoiceName selected={isSelected}>{voice.name || voice.identifier}</VoiceName>
                    <PlayButton playing={isPlaying} onClick={(e) => handlePreviewVoice(voice, e)}>
                      <i className={isPlaying ? 'mdi mdi-stop' : 'mdi mdi-play'} />
                    </PlayButton>
                  </VoiceItem>
                );
              })}
            </VoiceList>
          </VoiceSection>
        )}
      </Panel>
    </Overlay>
  );
});
